import { useQuery } from '@apollo/client'
import { useMemo } from 'react'
import { useSearchParams, useParams } from 'react-router'
import { gql } from '@/lib/graphql/gql'
import type {
  GetProjectIdDataQuery,
  GetProjectIdDataQueryVariables,
} from '@/lib/hooks/use-project-id.types.gen'

const GET_PROJECT_ID_DATA = gql`
  query GetProjectIdData(
    $idCompany: String!
    $appName: String!
    $staticSiteName: String!
    $databaseName: String!
    $idObjectStorage: String!
    $loadBalancerName: String!
    $isAppNameDefined: Boolean!
    $isStaticSiteNameDefined: Boolean!
    $isDatabaseNameDefined: Boolean!
    $isObjectStorageIdDefined: Boolean!
    $isLoadBalancerNameDefined: Boolean!
  ) {
    app(name: $appName) @include(if: $isAppNameDefined) {
      id
      idProject
    }
    staticSite(name: $staticSiteName) @include(if: $isStaticSiteNameDefined) {
      id
      idProject
    }
    database(name: $databaseName) @include(if: $isDatabaseNameDefined) {
      id
      idProject
    }
    company(id: $idCompany) {
      id
      objectStorage(id: $idObjectStorage)
        @include(if: $isObjectStorageIdDefined) {
        id
        idProject
      }
      edgeLoadBalancer(name: $loadBalancerName)
        @include(if: $isLoadBalancerNameDefined) {
        id
        idProject
      }
    }
  }
`

export const useIdProject = () => {
  const [searchParams] = useSearchParams()
  const {
    appName = '',
    staticSiteName = '',
    databaseName = '',
    idObjectStorage = '',
    loadBalancerName = '',
  } = useParams()

  const { data } = useQuery<
    GetProjectIdDataQuery,
    GetProjectIdDataQueryVariables
  >(GET_PROJECT_ID_DATA, {
    variables: {
      idCompany: searchParams.get('idCompany') ?? '',
      appName,
      staticSiteName,
      databaseName,
      idObjectStorage,
      loadBalancerName,
      isAppNameDefined: !!appName,
      isStaticSiteNameDefined: !!staticSiteName,
      isDatabaseNameDefined: !!databaseName,
      isObjectStorageIdDefined: !!idObjectStorage,
      isLoadBalancerNameDefined: !!loadBalancerName,
    },
    fetchPolicy: 'cache-first',
    skip:
      !appName &&
      !staticSiteName &&
      !databaseName &&
      !idObjectStorage &&
      !loadBalancerName &&
      !searchParams.get('idCompany'),
  })

  return useMemo(
    () => ({
      idProject:
        searchParams.get('idProject') ??
        data?.app?.idProject ??
        data?.staticSite?.idProject ??
        data?.database?.idProject ??
        data?.company?.objectStorage?.idProject ??
        data?.company?.edgeLoadBalancer?.idProject ??
        '',
    }),
    [searchParams, data]
  )
}
